if (!customElements.get('giga-brand-carousel')) {
  class GigaBrandCarousel extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.viewport = this.querySelector('.giga-brands__viewport');
      this.group = this.querySelector('.giga-brands__group');
      this.track = this.querySelector('.giga-brands__track');
      this.pauseButton = this.querySelector('[data-action="pause"]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.dataset.autoplay !== 'true' || this.reducedMotion.matches;
      this.inView = false;
      this.hovered = false;
      this.focused = false;
      this.cooldown = false;
      this.position = 0;
      this.speed = Number(this.dataset.speed) || 25;
      const signal = this.abort.signal;
      const clone = this.group.cloneNode(true);
      clone.dataset.clone = '';
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[data-shopify-editor-block]').forEach(item => item.removeAttribute('data-shopify-editor-block'));
      clone.querySelectorAll('a').forEach(link => link.setAttribute('tabindex', '-1'));
      this.track.append(clone);
      this.querySelector('.giga-brands__controls').hidden = false;
      this.refresh = () => {
        this.groupWidth = this.group.getBoundingClientRect().width;
        this.updatePlayback();
      };
      this.onMouseEnter = () => { this.hovered = true; this.updatePlayback(); };
      this.onMouseLeave = () => { this.hovered = false; this.updatePlayback(); };
      this.addEventListener('mouseenter', this.onMouseEnter, { signal });
      this.addEventListener('mouseleave', this.onMouseLeave, { signal });
      this.addEventListener('focusin', () => { this.focused = true; this.updatePlayback(); }, { signal });
      this.addEventListener('focusout', event => {
        this.focused = this.contains(event.relatedTarget);
        this.updatePlayback();
      }, { signal });
      this.viewport.addEventListener('pointerdown', () => this.rest(), { signal, passive: true });
      this.viewport.addEventListener('wheel', () => this.rest(), { signal, passive: true });
      this.viewport.addEventListener('keydown', () => this.rest(), { signal });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-action]');
        if (!button) return;
        if (button.dataset.action === 'pause') {
          this.paused = !this.paused;
          this.updateButton();
          this.updatePlayback();
          return;
        }
        this.rest();
        const distance = Math.max(128, this.viewport.clientWidth * 0.7);
        if (button.dataset.action === 'previous' && this.viewport.scrollLeft < 1) this.viewport.scrollLeft = this.groupWidth;
        this.viewport.scrollBy({ left: button.dataset.action === 'previous' ? -distance : distance, behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
      }, { signal });
      document.addEventListener('visibilitychange', () => this.updatePlayback(), { signal });
      this.motionChanged = () => {
        this.paused = this.reducedMotion.matches || this.dataset.autoplay !== 'true';
        this.updateButton();
        this.updatePlayback();
      };
      this.reducedMotion.addEventListener('change', this.motionChanged);
      this.intersection = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.updatePlayback();
      });
      this.intersection.observe(this);
      this.resize = new ResizeObserver(this.refresh);
      this.resize.observe(this.viewport);
      this.resize.observe(this.group);
      this.updateButton();
      this.refresh();
    }

    updateButton() {
      const label = this.paused ? this.pauseButton.dataset.labelPlay : this.pauseButton.dataset.labelPause;
      this.pauseButton.setAttribute('aria-label', label);
      this.pauseButton.setAttribute('title', label);
      this.pauseButton.setAttribute('aria-pressed', String(this.paused));
      this.pauseButton.querySelector('[data-icon="pause"]').hidden = this.paused;
      this.pauseButton.querySelector('[data-icon="play"]').hidden = !this.paused;
    }

    rest() {
      this.cooldown = true;
      clearTimeout(this.restTimer);
      this.updatePlayback();
      this.restTimer = setTimeout(() => {
        this.cooldown = false;
        this.updatePlayback();
      }, 10000);
    }

    updatePlayback() {
      cancelAnimationFrame(this.frame);
      if (this.paused || this.hovered || this.focused || this.cooldown || !this.inView || document.hidden || !this.groupWidth || this.groupWidth <= this.viewport.clientWidth) return;
      this.position = this.viewport.scrollLeft;
      this.previousTime = 0;
      const tick = time => {
        if (this.previousTime) {
          this.position += Math.min(time - this.previousTime, 64) / 1000 * this.speed;
          if (this.position >= this.groupWidth) this.position -= this.groupWidth;
          this.viewport.scrollLeft = this.position;
        }
        this.previousTime = time;
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      cancelAnimationFrame(this.frame);
      clearTimeout(this.restTimer);
      this.resize?.disconnect();
      this.intersection?.disconnect();
      this.reducedMotion?.removeEventListener('change', this.motionChanged);
      this.querySelector('[data-clone]')?.remove();
    }
  }
  customElements.define('giga-brand-carousel', GigaBrandCarousel);
}
